import type { PublicRecord } from '@/types/publicRecords';
import {
  buildReviewTrail,
  CERTIFICATE_DISCLAIMER,
  getCertificateId,
  getCertificateTitle,
  getProofCardId,
  getRecordDisplayLabel,
  getTrustLevel,
  TRUST_LEVEL_LABELS,
} from '@/lib/recordTrust';

export type CertificateDocumentMeta = {
  certificateId: string;
  proofCardId: string;
  title: string;
  certificateTitle: string;
  recordTitle: string;
  recordDescription: string;
  ownerName: string;
  ownerSubtitle?: string;
  ownerType: 'individual' | 'institution';
  pillar: string;
  locationName: string;
  eventDate: string;
  issuedDate: string;
  evidenceCount: number;
  verificationStatus: string;
  trustLabel: string;
  trustLevelLabel: string;
  verifyUrl: string;
  verifyPath: string;
  qrImageUrl: string;
  reviewStepsCompleted: string[];
  institutionReviewer?: string;
  disclaimer: string;
  integrityRef: string;
};

export function getSiteBaseUrl(): string {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return process.env.NEXT_PUBLIC_SITE_URL ?? 'https://ledger.ertzyx.com';
}

export function generateCertificateId(record: PublicRecord): string {
  const suffix = record.id.replace(/^rec-/, '').toUpperCase();
  const year = record.year ?? new Date(record.date).getFullYear();
  if (record.ownerType === 'institution') {
    return `ERT-CERT-INST-${year}-${suffix}`;
  }
  return `ERT-CERT-${year}-${suffix}`;
}

export function formatCertificateIssuedDate(iso?: string): string {
  const d = iso ? new Date(iso) : new Date();
  return d.toLocaleDateString('en-CA', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function getCertificateVerifyPath(certificateId: string): string {
  return `/verify?id=${encodeURIComponent(certificateId)}`;
}

export function getCertificateVerifyUrl(certificateId: string, baseUrl?: string): string {
  const base = baseUrl ?? getSiteBaseUrl();
  return `${base.replace(/\/$/, '')}${getCertificateVerifyPath(certificateId)}`;
}

export function getCertificateQrImageUrl(verifyUrl: string, size = 140): string {
  return `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&margin=8&data=${encodeURIComponent(verifyUrl)}`;
}

/** Short integrity reference derived from cert + proof ids (display only). */
export function buildIntegrityRef(certificateId: string, proofCardId: string): string {
  const raw = `${certificateId}:${proofCardId}`;
  let hash = 0;
  for (let i = 0; i < raw.length; i++) {
    hash = (hash << 5) - hash + raw.charCodeAt(i);
    hash |= 0;
  }
  return `ERT-CHK-${Math.abs(hash).toString(16).toUpperCase().padStart(8, '0').slice(0, 8)}`;
}

export function buildCertificateDocumentMeta(record: PublicRecord): CertificateDocumentMeta {
  const certificateId = getCertificateId(record) ?? generateCertificateId(record);
  const proofCardId = getProofCardId(record);
  const verifyUrl = getCertificateVerifyUrl(certificateId);
  const trust = getTrustLevel(record);
  const reviewSteps = buildReviewTrail(record)
    .filter((s) => s.completed)
    .map((s) => s.label);

  return {
    certificateId,
    proofCardId,
    title: getCertificateTitle(record),
    certificateTitle: getCertificateTitle(record),
    recordTitle: record.title,
    recordDescription: record.description,
    ownerName: record.ownerName,
    ownerSubtitle: record.ownerSubtitle,
    ownerType: record.ownerType,
    pillar: record.pillar,
    locationName: record.locationName,
    eventDate: formatCertificateIssuedDate(record.date),
    issuedDate: formatCertificateIssuedDate(),
    evidenceCount: record.evidenceCount,
    verificationStatus: record.verificationStatus,
    trustLabel: getRecordDisplayLabel(record),
    trustLevelLabel: TRUST_LEVEL_LABELS[trust].label,
    verifyUrl,
    verifyPath: getCertificateVerifyPath(certificateId),
    qrImageUrl: getCertificateQrImageUrl(verifyUrl, 160),
    reviewStepsCompleted: reviewSteps,
    institutionReviewer:
      record.ownerType === 'institution'
        ? record.ownerName
        : record.verificationStatus === 'Institution Verified' ||
            record.verificationStatus === 'Gold Verified'
          ? 'Institution reviewer on file'
          : undefined,
    disclaimer: CERTIFICATE_DISCLAIMER,
    integrityRef: buildIntegrityRef(certificateId, proofCardId),
  };
}

export const CERTIFICATE_ATTESTATION =
  'Ertzyx attests that the record named below was preserved with structured metadata, supporting evidence, and the review steps listed on this document.';

export const CERTIFICATE_NOT_ATTESTATION = [
  'Replace degrees, licenses, or IDs issued by schools, employers, or governments',
  'Prove legal identity or employment status on its own',
  'Guarantee outcomes beyond what evidence and review on Ertzyx support',
];
